import { useState } from 'react'
import { Panel, SectionHeader } from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { fmtNumber } from '@/shared/lib/format'
import { itemPrices, type ItemPrice } from './api'

/**
 * What things cost, and whether that has moved.
 *
 * ## The question a total cannot answer
 *
 * 375,000 on branding in August and 450,000 in October could be the same price for more
 * shirts, a higher price for the same shirts, or a different thing entirely. Those are three
 * different conversations to have with a supplier and the totals cannot tell them apart.
 * This page can: a t-shirt was 500 then and is 750 now.
 *
 * ## It says out loud that items are matched by name
 *
 * Grouping is on the typed label, so "tshirt" and "t-shirt" are two items. The page says so
 * rather than matching loosely — a page that silently merged two spellings would draw a flat
 * line for something that doubled, and nobody could tell which it had done.
 */
export function PricesPage() {
  const canAnalyse = useCan('finance:analyse')
  const [days, setDays] = useState(365)

  const to = new Date()
  const from = new Date(to.getTime() - days * 86_400_000)
  const iso = (d: Date) => d.toISOString().slice(0, 10)

  const prices = useLoad(() => itemPrices({ from: iso(from), to: iso(to) }), [days])

  if (!canAnalyse) return <Forbidden what="What things cost" />

  const items = prices.data ?? []

  return (
    <div className="space-y-4">
      <SectionHeader
        title="What things cost"
        description="Every item bought more than once, worst price movement first. Items are
                     matched by the name they were recorded under, so keep the spelling the
                     same when you record spending."
        right={(
          <div className="flex items-center gap-1">
            {[90, 180, 365].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setDays(n)}
                className={
                  'rounded border px-2 py-1 text-erp-xs '
                  + (n === days
                    ? 'border-foreground bg-muted font-medium'
                    : 'border-border text-muted-foreground hover:bg-muted/50')
                }
              >
                {n === 365 ? '1 year' : `${n} days`}
              </button>
            ))}
          </div>
        )}
      />

      <ErrorStrip message={prices.error} />

      {prices.loading && items.length === 0 ? (
        <LoadingBlock label="Working out what things cost…" />
      ) : items.length === 0 ? (
        <Panel title="Nothing to compare yet">
          <p className="p-3 text-erp-sm text-muted-foreground">
            This fills in as you record what you bought. On the <b>Record spending</b> form,
            fill in <b>what</b> you bought, <b>how many</b> and <b>the price of one</b> — then
            the next time you buy the same thing, the change in price appears here.
          </p>
        </Panel>
      ) : (
        <div className="space-y-3">
          {items.map((item) => <Item key={item.key} item={item} />)}
        </div>
      )}
    </div>
  )
}

function Item({ item }: { item: ItemPrice }) {
  const money = (n: number | null) =>
    n == null ? '—' : `${item.currency ?? ''} ${fmtNumber(Math.round(n))}`.trim()

  const rose = item.changePct != null && item.changePct > 0
  const fell = item.changePct != null && item.changePct < 0

  return (
    <Panel
      title={item.label}
      right={item.changePct == null ? (
        <span className="text-muted-foreground">
          {item.currency == null ? 'bought in more than one currency' : 'bought once'}
        </span>
      ) : (
        <span className={rose ? 'text-destructive' : fell ? 'text-emerald-600' : ''}>
          {rose ? '+' : ''}{item.changePct}%
        </span>
      )}
    >
      <div className="p-3">
        {item.currency == null ? (
          <p className="text-erp-sm text-muted-foreground">
            This was bought in more than one currency, so the prices cannot be compared.
            Converting them here would invent an exchange rate nobody agreed to.
          </p>
        ) : (
          <>
            <p className="text-erp-sm">
              {item.purchases === 1 ? (
                <>Bought once, at <b>{money(item.latestUnit)}</b> each.</>
              ) : (
                <>
                  Was <b>{money(item.firstUnit)}</b> on {item.firstOn}, now{' '}
                  <b>{money(item.latestUnit)}</b> on {item.latestOn}
                  {rose ? ' — it has gone up.' : fell ? ' — it has come down.' : '.'}
                </>
              )}
              {' '}
              {item.totalQuantity != null && item.totalQuantity > 0 ? (
                <>You have bought <b>{fmtNumber(item.totalQuantity)}</b> of them for{' '}
                  <b>{money(item.totalSpend)}</b> in all.</>
              ) : null}
            </p>

            {/* The cheapest price and who charged it — a percentage is a fact, this is the
                thing somebody can take to a supplier. */}
            {item.purchases > 1 && item.cheapestPaid != null
              && item.cheapestPaid !== item.dearestPaid ? (
              <p className="mt-1.5 text-erp-sm text-muted-foreground">
                Cheapest was <b>{money(item.cheapestPaid)}</b>
                {item.cheapestPayee ? <> from <b>{item.cheapestPayee}</b></> : null}; dearest
                was <b>{money(item.dearestPaid)}</b>
                {item.dearestPayee ? <> from <b>{item.dearestPayee}</b></> : null}.
              </p>
            ) : null}

            {item.history.length > 1 && (
              <div className="mt-3 space-y-1">
                {item.history.map((point, i) => {
                  const widest = Math.max(...item.history.map((h) => h.unitAmount))
                  return (
                    <div key={i} className="flex items-center gap-2 text-erp-xs">
                      <span className="w-20 shrink-0 tabular-nums text-muted-foreground">
                        {point.on}
                      </span>
                      <span className="h-3 rounded-[3px] bg-[var(--chart-1)]"
                            style={{ width: `${Math.max(2, (point.unitAmount / widest) * 60)}%` }} />
                      <span className="tabular-nums">{money(point.unitAmount)}</span>
                      {point.quantity != null ? (
                        <span className="text-muted-foreground">
                          × {fmtNumber(point.quantity)}
                        </span>
                      ) : null}
                      {point.payee ? (
                        <span className="truncate text-muted-foreground">{point.payee}</span>
                      ) : null}
                    </div>
                  )
                })}
              </div>
            )}
          </>
        )}
      </div>
    </Panel>
  )
}
